import { useCallback, useMemo, useRef } from 'react';

import type { ClaimWorkItem } from '../../../store';
import {
  CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS,
  useClaimWorkspacePrefetch,
} from '../../../utils/claimWorkspacePrefetch';

/**
 * Hover prefetch for worklist rows (GDN-3201): warm the workbench's first
 * fetch wave — the claim-workspace GET (~2.35s at the production median,
 * GDN-3199), the claim record, and the certification — while the biller's
 * cursor is still deciding. RTK Query's own prefetch machinery does the
 * work, so this never invents cache entries: a real request warms the real
 * cache, or nothing happens.
 *
 * The three prefetches themselves now live in `useClaimWorkspacePrefetch`,
 * shared with the claim queue's arrival prefetch (GDN-3322). What stays here
 * is what is true of HOVER specifically:
 * - Once per row per mount: one biller sweep across the list must not
 *   re-fire on every re-entry; the TTL alone can't stop the burst of
 *   enter/leave/enter on the same row.
 * - Hover-capable pointers only: on touch devices the "hover" is the tap
 *   itself — prefetching there would fire alongside the navigation it was
 *   meant to precede, doubling requests for nothing.
 *
 * The active-batch skip lives in the CALLER (ClaimsTab), which owns the
 * batch state; this hook stays ignorant of batches by design.
 */

/** Don't re-request data fresher than this. Matches the intent of a hover:
 *  warm a COLD cache for an imminent click, not keep a hot one hot.
 *  Re-exported from the shared core so existing importers keep their name. */
export const WORKLIST_PREFETCH_TTL_SECONDS = CLAIM_WORKSPACE_PREFETCH_TTL_SECONDS;

export interface WorklistRowPrefetch {
  prefetchRow: (item: ClaimWorkItem) => void;
  /** Whether a prefetch was ATTEMPTED for this row this mount — the
   *  `prefetched` flag on billing_claim_worklist_row_clicked, the
   *  optimization's own meter (hit rate, not just adoption). */
  wasPrefetched: (claimId: string) => boolean;
}

export function useWorklistRowPrefetch(): WorklistRowPrefetch {
  const prefetchClaimWorkspace = useClaimWorkspacePrefetch();

  /* Evaluated once per mount: pointer capability doesn't change mid-session,
     and a matchMedia call per hover would be waste. `?? false` covers jsdom
     and any environment without matchMedia — no hover, no prefetch. */
  const canHover = useMemo(
    () => window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false,
    [],
  );
  const seenRef = useRef(new Set<string>());

  const prefetchRow = useCallback(
    (item: ClaimWorkItem) => {
      if (!canHover) return;
      if (seenRef.current.has(item.claim_id)) return;
      seenRef.current.add(item.claim_id);
      prefetchClaimWorkspace({
        claimId: item.claim_id,
        encounterId: item.encounter_id ?? null,
      });
    },
    [canHover, prefetchClaimWorkspace],
  );

  const wasPrefetched = useCallback((claimId: string) => seenRef.current.has(claimId), []);

  return useMemo(() => ({ prefetchRow, wasPrefetched }), [prefetchRow, wasPrefetched]);
}
